import React from 'react';
import './BottomNav.css';

/**
 * BottomNav tab IDs — 1:1 with RN's FloatingToolbar nav_v2 layout
 * (`apps/WunderGuide/views/Dashboard.tsx` toolbarActions, isNavV2 branch):
 *   Trip | Explore | Book | Deals | Account
 */
export type BottomNavTab = 'trip' | 'explore' | 'book' | 'deals' | 'account';

export interface NavItem {
  id: BottomNavTab;
  icon: React.ReactNode;
  label: string;
}

interface BottomNavProps {
  items: NavItem[];
  active?: BottomNavTab | null;
  onChange?: (tab: BottomNavTab) => void;
}

/**
 * Bottom 5-icon nav from screenshots 00–05. Capsule pill, glass-morphism,
 * fixed to viewport bottom with safe-area padding.
 *
 * `items` is now a prop — Dashboard computes it from feature flags so the
 * visible tab set matches RN's FloatingToolbar gating (RN hides any tab
 * whose flag is off).
 */
const BottomNav: React.FC<BottomNavProps> = ({ items, active, onChange }) => {
  if (items.length === 0) return null;
  return (
    <nav className="wg-bottomnav" role="navigation" aria-label="Primary">
      <div className="wg-bottomnav__pill">
        {items.map(item => (
          <button
            key={item.id}
            type="button"
            className={`wg-bottomnav__btn${active === item.id ? ' is-active' : ''}`}
            onClick={() => onChange?.(item.id)}
            aria-label={item.label}
          >
            <span className="wg-bottomnav__icon">{item.icon}</span>
          </button>
        ))}
      </div>
    </nav>
  );
};

/**
 * Default 5-tab nav matching RN nav_v2 (Dashboard.tsx toolbarActions,
 * `isNavV2` branch): calendar→Trip, map→Explore, search→Book, tag→Deals,
 * person→Account. Localised at call-site via `buildDefaultNavItems(t)` so
 * the labels follow the active language.
 */
export type Translate = (key: string) => string | undefined;

export const buildDefaultNavItems = (t: Translate): NavItem[] => [
  { id: 'trip', icon: <CalendarIcon />, label: t('TRIP') || 'Trip' },
  { id: 'explore', icon: <MapIcon />, label: t('EXPLORE') || 'Explore' },
  { id: 'book', icon: <SearchIcon />, label: t('BOOK') || 'Book' },
  { id: 'deals', icon: <TagIcon />, label: t('DEALS') || 'Deals' },
  { id: 'account', icon: <UserIcon />, label: t('ACCOUNT') || 'Account' },
];

/** @deprecated Use `buildDefaultNavItems(t)` so labels translate. */
export const defaultNavItems: NavItem[] = buildDefaultNavItems(
  (() => undefined) as Translate,
);

export default BottomNav;

function CalendarIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="4" width="18" height="17" rx="2.5" />
      <line x1="3" y1="9.5" x2="21" y2="9.5" />
      <line x1="8" y1="2.5" x2="8" y2="6" />
      <line x1="16" y1="2.5" x2="16" y2="6" />
    </svg>
  );
}

function MapIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polygon points="2,6 9,3 15,6 22,3 22,18 15,21 9,18 2,21" />
      <line x1="9" y1="3" x2="9" y2="18" />
      <line x1="15" y1="6" x2="15" y2="21" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="10.5" cy="10.5" r="6.5" />
      <line x1="20" y1="20" x2="15.5" y2="15.5" />
    </svg>
  );
}

function TagIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20.6 12.6 12 21.2c-.78.78-2.05.78-2.83 0L2.5 14.5c-.32-.32-.5-.75-.5-1.2V4a2 2 0 0 1 2-2h9.3c.45 0 .88.18 1.2.5l6.1 6.1c.78.78.78 2.05 0 2.83Z" />
      <circle cx="7.5" cy="7.5" r="1.5" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="8.5" r="4" />
      <path d="M4 21c1.4-4 4.4-6 8-6s6.6 2 8 6" />
    </svg>
  );
}
